<template>
	<VDataGridV2
		ref="grid"
		entity-type="store"
		sticky
		paginated

		:loader="loader"
		:hydrate-constructor="Store.fromServer.bind(Store)"
		:skeleton-row-count="40"

		@row:contextmenu="openAddZonePopup"
		@row:dblclick="openStoreCard"
	>
		<VDataGridColumnV2
			id="id"
			width="75px"
			content-align="left"
			label="№"
		>
			<template #default="{ row }">
				<VButton
					theme="link"
					@click="openStoreCard(row)"
				>
					{{ row.id }}
				</VButton>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="partner"
			width="250px"
			label="Партнер"
			filterable
			filter-send-name="pattern"
			:filter-props="{
      	placeholder: 'Введите название',
				clearable:   true,
			}"
			content-class="nowrap ellipsis"
		>
			<template #default="{ row }">
				<span :title="row.partner?.name">
					{{ row.partner?.name }}
				</span>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="name"
			width="400px"
			label="Склад"
			content-class="nowrap ellipsis"
		>
			<template #default="{ row }">
				<span :title="row.name">
					{{ row.name }}
				</span>
				<div class="description">
					<VBadge
						v-if="row.isDraft"
						class="draft"
					>
						Черновик
					</VBadge>
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="transportZone"
			width="150px"
			label="Транспортная зона"
			sortable
			sort-default-value="asc"
			header-class="nowrap"
      content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.transportZone ? row.transportZone.name : '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="hasTransportZone"
			width="120px"
			label="Есть зона"
			filterable
			filter-type="select"
			filter-identity="hasTransportZone"
			header-class="nowrap"
			:filter-props="{
      	options:     StoresService.HAS_TRANSPORT_ZONE_OPTIONS,
				placeholder: 'Все',
				clearable:   true,
			}"
			:filter-default-value="StoresService.HAS_TRANSPORT_ZONE_OPTIONS[1]"
		>
			<template #default="{ row }">
				{{ row.transportZone ? 'Да' : 'Нет' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isDraft"
			width="120px"
			label="Черновик"
			filterable
			filter-type="select"
			filter-identity="isDraft"
			:filter-props="{
      	options:     StoresService.IS_DRAFT_OPTIONS,
				placeholder: 'Все',
				clearable:   true,
			}"
			:filter-default-value="StoresService.IS_DRAFT_OPTIONS[0]"
		>
			<template #default="{ row }">
				{{ row.isDraft ? 'Да' : 'Нет' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="action"
		>
			<template #default="{ row }">
				<VKebabButton>
					<StoresContextMenu
						:store="row"
						@store:updated="onListChanged"
						@store:deleted="onListChanged"
					/>
				</VKebabButton>
			</template>
		</VDataGridColumnV2>
	</VDataGridV2>

	<!-- Кастомный локальный popup для быстрого назначения транспортной зоны по right-click.
			 Никакой существующий popup для этих целей не подходит — быстрее сделать такой. -->
	<div
		v-if="transportZonePopup.visible"
		ref="change-transport-zone"
		class="mds-popup reason-refuse-popup"
		:class="{'visible': transportZonePopup.visible}"
		:style="transportZonePopup.dialogStyle"
	>
		<VSelect
			v-if="transportZonePopup.visible"
			ref="transportZoneControl"
			layout="vertical"
			data-url="transportZones.getList"
			label="Транспортная зона"
			placeholder="Введите транспортную зону"
			v-model="transportZonePopup.store.transportZone"
			@update:modelValue="onTransportZoneInput"
		/>
	</div>
</template>

<script lang="ts">

import Store             from '@/domains/Stores/Store';
import StoresService     from '@/domains/Stores/StoresService';
import DataGridLoader    from '@/lib/components/VDataGridV2/DataGridLoader';

import StoresContextMenu  from '@/domains/Stores/components/StoresContextMenu/StoresContextMenu.vue';

export default defineComponent({
	name: 'StoresRegistry',

	components: {
		StoresContextMenu,
	},

	data() {
		return {
			Store,
      StoresService,

			contextMenus: {} as Record<number, boolean>,

			loader: new DataGridLoader(StoresService.getListPaginated.bind(StoresService)),

			transportZonePopup: {
				visible:     false,
				store:       null as Store | null,
				dialogStyle: null as Record<string, any> | null,
			},
		};
	},

	created() {
		window.addEventListener('click', this.onClickOutsideCustom);
	},

	beforeUnmount() {
		window.removeEventListener('click', this.onClickOutsideCustom);
	},

	methods: {
		openStoreCard(row: Store) {
			StoresService.editStore(row, this.onListChanged);
		},

		/** Любое изменение в списке (создан/обновлён/удалён склад) — перезагрузить грид */
		onListChanged() {
			this.loader.refresh();
		},

		async onTransportZoneInput(value: any) {
			const updatedStore = {
				...this.transportZonePopup.store,
				transportZone: value,
			} as Store;

			await StoresService.update(updatedStore);

			this.closeTransportZonePopup();
			this.onListChanged();
		},

		async openAddZonePopup(row: Store, event: MouseEvent) {
			/** Повторный right-click по той же строке — закрываем попап */
			if (row.id === this.transportZonePopup.store?.id) {
				this.closeTransportZonePopup();
				return;
			}

			this.closeTransportZonePopup();

			await this.$nextTick();
			this.showTransportZonePopup(event, row);
		},

		async showTransportZonePopup(event: MouseEvent, entity: Store) {
			this.transportZonePopup.store = entity;

			/** Отодвигаем меню на 5px от курсора, иначе при повторном клике в ту же позицию
			 * попадаешь в попап и вылезает нативное меню браузера */
			this.transportZonePopup.dialogStyle = {
				zIndex:  70,
				padding: '10px',
				left:    `${event.clientX + 5}px`,
				top:     `${event.clientY + 5}px`,
				display: 'block',
			};

			this.transportZonePopup.visible = true;

			await this.$nextTick();
			(this.$refs.transportZoneControl as any).doFocus();
		},

		closeTransportZonePopup() {
			this.transportZonePopup.store   = null;
			this.transportZonePopup.visible = false;
		},

		onClickOutsideCustom(event: MouseEvent) {
			if (!this.transportZonePopup.visible) return;

			const popupEl = this.$refs['change-transport-zone'] as HTMLElement | undefined;
			if (popupEl && !popupEl.contains(event.target as Node)) {
				this.closeTransportZonePopup();
			}
		},
	},
});
</script>
